import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { AuthLayout } from "@/components/auth-layout";
import { Button } from "@/components/button";
import { Icon } from "@/components/icons";
import { Form, FormField, FormSubmit } from "@/src/forms";
import { confirmPasswordReset } from "@/src/services/auth_service";
import { resetPasswordSchema, type ResetPasswordValues } from "./_schemas/reset-password";

/**
 * The user lands here after clicking the link in a password-reset email. The
 * link is expected to carry `?uid=&token=` (the dj-rest-auth reset params);
 * `confirmPasswordReset` posts them with the new password to
 * `POST /api/users/password-reset/confirm/`. (Whether the backend targets this
 * SPA route vs. its own page is an open Q to Alejandro — if it targets its own
 * page this route is simply never reached, which is harmless.)
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const uid = params.get("uid") ?? "";
  const token = params.get("token") ?? "";
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // No reset params → the link is malformed/expired; can't complete the reset.
  const linkValid = Boolean(uid && token);

  async function handleValid(values: ResetPasswordValues) {
    setError(null);
    const res = await confirmPasswordReset({
      uid,
      token,
      newPassword1: values.password,
      newPassword2: values.confirm,
    });
    if (!res.ok) {
      setError(res.error.message);
      return;
    }
    setDone(true);
    setTimeout(() => navigate("/login", { replace: true }), 2000);
  }

  if (!linkValid) {
    return (
      <AuthLayout>
        <div className="popup w-[400px] rounded-[8px]">
          <div className="flex flex-col gap-4">
            <h1 className="text-h4 text-black">Reset link expired or invalid</h1>
            <p className="text-small text-grey-900">
              This password-reset link is no longer valid. Request a new one.
            </p>
            <Button hierarchy="primary" size="md" onClick={() => navigate("/forgot-password")}>
              Request new link
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="popup w-[400px] rounded-[8px]">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <h1 className="text-h4 text-black">Create a new password</h1>
            <p className="text-small text-grey-900">
              {done
                ? "Your password has been updated. Redirecting you to login…"
                : "Please enter a password that is at least 8 characters long."}
            </p>
          </div>

          {!done && (
            <Form
              schema={resetPasswordSchema}
              defaultValues={{ password: "", confirm: "" }}
              onSubmit={handleValid}
              className="flex flex-col gap-4"
            >
              <FormField
                name="password"
                label="New password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="••••••••••"
                rightIcon={
                  <PwToggle shown={showPassword} onToggle={() => setShowPassword((v) => !v)} />
                }
              />
              <FormField
                name="confirm"
                label="Confirm new password"
                type={showConfirm ? "text" : "password"}
                autoComplete="new-password"
                placeholder="••••••••••"
                rightIcon={
                  <PwToggle shown={showConfirm} onToggle={() => setShowConfirm((v) => !v)} />
                }
              />

              {error && (
                <div
                  role="alert"
                  className="rounded-[4px] border border-red-500 bg-red-100 px-3 py-2 text-small text-red-600"
                >
                  {error}
                </div>
              )}

              <FormSubmit
                hierarchy="primary"
                size="md"
                className="w-full"
                pendingLabel="Saving…"
                rightIcon={<Icon name="arrow_forward" size={20} />}
              >
                Reset password
              </FormSubmit>
            </Form>
          )}
        </div>
      </div>
    </AuthLayout>
  );
}

function PwToggle({ shown, onToggle }: { shown: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={shown ? "Hide password" : "Show password"}
      className="flex"
    >
      <Icon name={shown ? "visibility_off" : "visibility"} size={20} />
    </button>
  );
}
